import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Modal,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useGetBranches } from "src/api/generic";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Papa from "papaparse";
import FileUploader from "src/shared/components/file-uploader";
import { useEffect, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { notificationsState } from "src/store/notifications";
import { useImportCountMutation } from "src/api/inventory/counts";
import {
  convertObjectToFormData,
  downloadCsvTemplate,
  specificBranchHasPermission,
} from "src/utils";
import { LoadingButton } from "@mui/lab";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import moment from "moment";
import { InventoryPermissions } from "src/constants/permissions";
import { AWS_URLS } from "src/constants/aws-urls";
import { authState } from "src/store/auth";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

interface IModel {
  open: boolean;
  handleClose: () => void;
}

const schema = yup.object().shape({
  branch_id: yup.number().required("required"),
  uploaded_file: yup.string().required("required"),
  date: yup.date().required("required"),
  isTheOpeningBalanceDate: yup.boolean(),
  openingBalanceType: yup.string().when("isTheOpeningBalanceDate", {
    is: true,
    then: (value) => value.required("Required"),
  }),
});

export interface FormInputs extends yup.InferType<typeof schema> {}

const ImportCountPopup = (props: IModel) => {
  const { open, handleClose } = props;
  const navigate = useNavigate();
  const { t } = useTranslation("inventory");
  const { t: addSalesT } = useTranslation("addSales");
  const { t: generalT } = useTranslation("general");
  const auth = useRecoilValue(authState);
  const user = auth?.user;
  const company = user?.company;
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [uploadedFile, setUploadedFile] = useState<any>();
  const [fileData, setFileData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // APIS
  const { data: branchesData } = useGetBranches();
  const { mutate, status, error, data } = useImportCountMutation();

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<FormInputs>({
    mode: "onBlur",
    resolver: yupResolver(schema),
  });

  // permissions
  const confirmPermission = specificBranchHasPermission(
    InventoryPermissions.confirmStocktaking,
    +watch("branch_id"),
  );
  const branchesHavePermission =
    branchesData?.data.filter(
      (branch) =>
        branch.status &&
        specificBranchHasPermission(
          InventoryPermissions.addStocktaking,
          branch.id,
        ),
    ) || [];

  const handleFileUpload = (data: any) => {
    console.log({ data });
    setUploadedFile(data ?? undefined);
    if (data) {
      Papa.parse(data, {
        header: true,
        complete: (results: any) => {
          console.log({ results });
          setFileData(results.data);
        },
        skipEmptyLines: true,
      });
    }
  };

  const selectedBranch = branchesData?.data.find((branch) => {
    return branch.id === +watch("branch_id");
  });

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    //no file
    if (!uploadedFile) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: addSalesT("uploadFile"),
        },
      ]);
      return;
    }
    //empty file
    if (fileData.length === 0) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: addSalesT("emptyFile"),
        },
      ]);
      return;
    }

    const { branch_id, date, isTheOpeningBalanceDate, openingBalanceType } =
      data;

    // Balance date validation
    if (
      !!company?.start_balance_date &&
      moment(date)
        .startOf("day")
        .isBefore(moment(company.start_balance_date).startOf("day"))
    ) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: `${generalT("balanceDateValidationMsg")}: ${
            company?.start_balance_date
          }`,
        },
      ]);
      return;
    }

    // Lock date validation
    if (
      !!selectedBranch?.close_date &&
      moment(date)
        .startOf("day")
        .isSameOrBefore(moment(selectedBranch.close_date).startOf("day"))
    ) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: `${generalT("canNotAddEditOrDeleteBeforeLockDate")}: ${
            selectedBranch?.close_date
          }`,
        },
      ]);
      return;
    }

    const formDataToSend = convertObjectToFormData({
      status: "draft",
      branch_id: branch_id,
      date: moment(date).format("YYYY-MM-DD HH:mm:ss"),
      ...(isTheOpeningBalanceDate
        ? { is_opening_balance: openingBalanceType === "yes" ? 1 : 0 }
        : {}),
    });

    formDataToSend.append("file", uploadedFile);

    setIsLoading(true);
    mutate(formDataToSend);
  };

  //Add success and error handling
  useEffect(() => {
    if (status === "success") {
      const delayedExecution = setTimeout(() => {
        setNotifications([
          ...notifications,
          {
            type: "success",
            message: generalT("addedSuccessfully"),
          },
        ]);
        setIsLoading(false);
        navigate(`/counts/${data?.data.id}/edit`);
      }, 10000);

      return () => clearTimeout(delayedExecution);
    } else if (status === "error") {
      setIsLoading(false);
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data.errors?.[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [status]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Stack
        spacing={3}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 537,
          bgcolor: "#FFF",
          border: "1px solid #D0D5DD",
          borderRadius: 1,
          boxShadow: 24,
          p: 3,
        }}
      >
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
          p={0}
        >
          <Typography color={"#101828"} fontSize={"20px"}>
            {t("importStocktaking")}
          </Typography>
          <IconButton onClick={handleClose} sx={{ p: 0 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            <FormControl fullWidth size="small" error={!!errors.branch_id}>
              <InputLabel id="demo-simple-select-label">
                {generalT("branch")}
              </InputLabel>
              <Controller
                name="branch_id"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    key={field.value}
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    label={generalT("branch")}
                  >
                    {branchesHavePermission?.map(
                      ({ id, name, active_subscriptions }) => {
                        return (
                          <MenuItem
                            key={id}
                            value={`${id}`}
                            disabled={!active_subscriptions?.package.has_items}
                          >
                            {name}
                          </MenuItem>
                        );
                      },
                    )}
                  </Select>
                )}
              />
              <FormHelperText id="my-helper-text">
                {generalT(`${errors.branch_id?.message ?? ""}`)}
              </FormHelperText>
            </FormControl>
            <Controller
              name="date"
              control={control}
              render={({ field }) => (
                <DateTimePicker
                  {...field}
                  onChange={(value) => {
                    field.onChange(value);
                    const isSameDate =
                      !!company?.start_balance_date &&
                      moment(value).isSame(
                        moment(company?.start_balance_date, "YYYY-MM-DD"),
                        "day",
                      );
                    setValue("isTheOpeningBalanceDate", isSameDate);
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.date,
                      helperText: generalT(`${errors.date?.message ?? ""}`),
                      size: "small",
                    },
                  }}
                  format={"dd/MM/yyyy hh:mm aa"}
                  label={t("date")}
                />
              )}
            />

            {watch("isTheOpeningBalanceDate") && (
              <Stack
                direction={"row"}
                alignItems={"center"}
                gap={{ xs: 1, sm: 1, md: 3 }}
                flexWrap={"wrap"}
                width={"100%"}
              >
                <Typography color={"textSecondary"}>
                  {t("isThisOpeningBalance")}
                </Typography>
                <FormControl
                  fullWidth
                  size="small"
                  error={!!errors.openingBalanceType}
                  sx={{
                    width: "fit-content",
                    ml: { xs: "auto", sm: "unset" },
                  }}
                >
                  <Controller
                    name="openingBalanceType"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup
                        {...field}
                        key={field.value}
                        row
                        aria-labelledby="demo-row-radio-buttons-group-label"
                        name="row-radio-buttons-group"
                      >
                        <FormControlLabel
                          value="yes"
                          control={
                            <Radio
                              icon={<CircleOutlinedIcon />}
                              checkedIcon={<CheckCircleIcon />}
                            />
                          }
                          label={generalT("yes")}
                          sx={{ color: "#98A2B3" }}
                        />
                        <FormControlLabel
                          value="no"
                          control={
                            <Radio
                              icon={<CircleOutlinedIcon />}
                              checkedIcon={<CheckCircleIcon />}
                            />
                          }
                          label={generalT("no")}
                          sx={{ color: "#98A2B3" }}
                        />
                      </RadioGroup>
                    )}
                  />
                  <FormHelperText id="my-helper-text">
                    {errors.openingBalanceType?.message}
                  </FormHelperText>
                </FormControl>
              </Stack>
            )}
          </Stack>

          {!!watch("branch_id") ? (
            <>
              <Box my={1.5}>
                <Typography mb={1}>
                  {addSalesT("toUploadYourSalesFromExcel")}
                </Typography>
                <Stack direction={"row"} alignItems={"center"} gap={0.8}>
                  <Box
                    bgcolor={"#000"}
                    width={"4px"}
                    height={"4px"}
                    borderRadius={"50%"}
                  />
                  <Typography>{addSalesT("downloadOur")}</Typography>
                  <Link
                    color={"#1A1AE6"}
                    href={
                      selectedBranch?.has_menu
                        ? AWS_URLS.IMPORT_STOCKTAKING
                        : AWS_URLS.IMPORT_STOCKTAKING_NO_RECIPE
                    }
                    sx={{ fontSize: "1rem", fontWeight: 500 }}
                  >
                    {addSalesT("template")}.
                  </Link>
                </Stack>
                <Stack direction={"row"} alignItems={"center"} gap={0.8}>
                  <Box
                    bgcolor={"#000"}
                    width={"4px"}
                    height={"4px"}
                    borderRadius={"50%"}
                  />
                  <Typography>{addSalesT("fulfillRequiredColumns")}</Typography>
                </Stack>
                <Stack direction={"row"} alignItems={"center"} gap={0.8}>
                  <Box
                    bgcolor={"#000"}
                    width={"4px"}
                    height={"4px"}
                    borderRadius={"50%"}
                  />
                  <Typography>{addSalesT("uploadUpdatedFileHere")}</Typography>
                </Stack>
              </Box>

              <FileUploader
                label={""}
                onFileUpload={(data) => handleFileUpload(data)}
                onFileDelete={() => {
                  setUploadedFile(undefined);
                  setValue("uploaded_file", "");
                }}
                id="uploaded_file"
                name="uploaded_file"
                control={control}
                error={!!errors.uploaded_file}
                helperText={errors.uploaded_file?.message}
                isLoading={false}
                value={uploadedFile}
                disabled={!watch("branch_id")}
              />
            </>
          ) : null}

          <Stack mt={3} spacing={2} direction={"row"}>
            <LoadingButton
              variant="outlined"
              color="tertiary"
              fullWidth
              onClick={handleClose}
              disabled={isLoading}
            >
              {generalT("cancel")}
            </LoadingButton>
            <LoadingButton
              loading={isLoading}
              type="submit"
              variant="contained"
              fullWidth
            >
              {generalT("submit")}
            </LoadingButton>
          </Stack>
        </form>
      </Stack>
    </Modal>
  );
};

export default ImportCountPopup;
